/**
 * TDBMissingDaysTicker — at-a-glance roll of dates that are missing
 * Store / Swiggy / Zomato / B2B / Delivery uploads on TDB.
 *
 * Data source: GET /api/tdb/missing-channels?lookback_days=14
 *   Returns `missing_by_date` (newest first), `complete_pct`,
 *   `fully_empty_days`, `expected_cells`.
 *
 * Behaviour:
 *   • If complete_pct ≥ 99 → renders a small green "all caught up" pill.
 *   • Otherwise renders an amber/red banner + a horizontal scroll-strip
 *     of up to 14 day-cards. Each card lists the missing (city · channel)
 *     pairs and exposes two CTAs:
 *       1. "Copy WA template"  → puts a templated Mira command on the
 *          clipboard so the leader can paste it into WhatsApp + Mira.
 *       2. "Open in Mira"      → navigates to /admin?tab=mira&prefill=…
 *          which AdminPanel honours by pre-filling the chat input.
 *
 * Placement: sits on the TDB DivisionDetail page directly above the
 * <ChannelMatrix /> so anyone landing on TDB sees the gaps first.
 *
 * Access: same `_has_tdb_access` gate as the backend route — non-TDB
 * users will get 403 → the component degrades silently to null.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Clipboard, ArrowRight, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const fmtDateShort = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', weekday: 'short' });
};

const CITY_CODE = { Bengaluru: 'blr', Mumbai: 'mum', Gurgaon: 'ggn' };

// Build a single-line Mira command for one missing day.
// Mira's parser understands "tdb 2026-05-15 store blr 0, swiggy blr 0,
// zomato mum 4500" (see operator_mira_routes.py prompt). We hand the
// leader a paste-ready template with zero placeholders so they only
// have to replace the 0s with the real numbers.
function buildMiraCommand(date, gaps) {
  const segments = gaps.map((g) => {
    const [city, channel] = g.split(' ');
    const code = CITY_CODE[city] || (city || '').toLowerCase().slice(0, 3);
    return `${channel.toLowerCase()} ${code} 0`;
  });
  return `tdb ${date} ${segments.join(', ')}`;
}

export default function TDBMissingDaysTicker() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const r = await api.get('/tdb/missing-channels?lookback_days=14');
      setData(r.data);
    } catch (e) {
      // 403 for non-TDB users → render nothing (handled by parent gate too).
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyTemplate = (date, gaps) => {
    const cmd = buildMiraCommand(date, gaps);
    navigator.clipboard.writeText(cmd).then(
      () => toast.success(`Copied to clipboard · paste into Mira or WhatsApp`, { description: cmd }),
      () => toast.error('Could not copy — select & copy manually', { description: cmd }),
    );
  };

  const openInMira = (date, gaps) => {
    const cmd = buildMiraCommand(date, gaps);
    navigate(`/admin?tab=mira&prefill=${encodeURIComponent(cmd)}`);
  };

  if (loading) {
    return (
      <div className="rounded-md border border-dashed px-3 py-2 text-xs text-[#9CA3AF]" style={{ borderColor: '#E5E1DC' }}
        data-testid="missing-days-ticker-loading">
        Checking which TDB days still need uploads…
      </div>
    );
  }
  if (data?.forbidden) return null;
  if (error) {
    return (
      <div className="rounded-md px-3 py-2 text-xs"
        style={{ backgroundColor: '#FCEBEB', color: '#A32D2D', border: '1px solid #E8B4B4' }}
        data-testid="missing-days-ticker-error">
        Missing-days ticker error: {error}
      </div>
    );
  }
  if (!data?.missing_by_date) return null;

  const totalGaps = data.missing_count || 0;
  const completePct = data.complete_pct != null ? data.complete_pct : 100;

  // All caught up — small green pill, no scroll-strip.
  if (totalGaps === 0) {
    return (
      <div className="flex items-center gap-2 rounded-md px-3 py-2 text-xs"
        style={{ backgroundColor: '#E8F7F4', color: '#0F766E', border: '1px solid #BAE7DC' }}
        data-testid="missing-days-ticker-clean">
        <CheckCircle2 size={14} />
        <span className="font-semibold">All caught up.</span>
        <span className="text-[#0F766E]/80">
          {data.scan_window?.start} → {data.scan_window?.end} · every city × channel cell has an entry.
        </span>
      </div>
    );
  }

  const severity = completePct < 60 ? 'red' : 'amber';
  const colors = severity === 'red'
    ? { bg: '#FCEBEB', fg: '#A32D2D', border: '#E8B4B4', chip: '#A32D2D' }
    : { bg: '#FEF6E4', fg: '#92400E', border: '#FACC15', chip: '#92400E' };

  const days = data.missing_by_date.slice(0, 14);

  return (
    <div className="rounded-md" style={{ backgroundColor: colors.bg, border: `1.5px solid ${colors.border}` }}
      data-testid="missing-days-ticker">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-4 py-2.5 border-b" style={{ borderColor: colors.border }}>
        <div className="flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5 flex-shrink-0" style={{ color: colors.fg }} />
          <div>
            <p className="text-sm font-semibold" style={{ color: colors.fg }}>
              {totalGaps} cell{totalGaps === 1 ? '' : 's'} missing across {days.length} day{days.length === 1 ? '' : 's'}
              <span className="ml-2 text-xs font-normal opacity-80">
                ({completePct}% of last {data.scan_window?.start ? '14d' : 'period'} captured)
              </span>
            </p>
            <p className="text-[11px] mt-0.5" style={{ color: colors.fg, opacity: 0.85 }}>
              Each card below builds a paste-ready Mira command — fix the gaps in seconds.
            </p>
          </div>
        </div>
        <button
          onClick={() => { setRefreshing(true); load(true); }}
          disabled={refreshing}
          className="text-[11px] flex items-center gap-1 px-2 py-1 rounded border hover:bg-white/40 disabled:opacity-50"
          style={{ borderColor: colors.border, color: colors.fg }}
          data-testid="missing-days-refresh">
          <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Horizontal scroll strip of day cards */}
      <div className="flex gap-2 overflow-x-auto px-3 py-3" data-testid="missing-days-strip">
        {days.map((dayRow) => (
          <DayCard
            key={dayRow.date}
            date={dayRow.date}
            gaps={dayRow.gaps}
            isFullyEmpty={(data.fully_empty_days || []).includes(dayRow.date)}
            onCopy={() => copyTemplate(dayRow.date, dayRow.gaps)}
            onOpen={() => openInMira(dayRow.date, dayRow.gaps)}
            severity={severity}
          />
        ))}
      </div>
    </div>
  );
}

function DayCard({ date, gaps, isFullyEmpty, onCopy, onOpen, severity }) {
  const accent = isFullyEmpty || severity === 'red' ? '#A32D2D' : '#92400E';

  return (
    <div className="flex-shrink-0 w-[230px] rounded-md bg-white p-3 shadow-sm border"
      style={{ borderColor: isFullyEmpty ? '#E8B4B4' : '#FACC15' }}
      data-testid={`missing-day-card-${date}`}>
      <div className="flex items-baseline justify-between mb-1.5">
        <p className="text-sm font-semibold text-[#1B2A4A]" data-testid={`missing-day-date-${date}`}>
          {fmtDateShort(date)}
        </p>
        <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full"
          style={{ backgroundColor: isFullyEmpty ? '#FCEBEB' : '#FEF6E4', color: accent }}>
          {isFullyEmpty ? 'Empty day' : `${gaps.length} gap${gaps.length === 1 ? '' : 's'}`}
        </span>
      </div>

      <ul className="text-[11px] space-y-0.5 mb-2 text-[#6B7280]" data-testid={`missing-day-gaps-${date}`}>
        {gaps.slice(0, 4).map((g) => (
          <li key={g} className="flex items-center gap-1">
            <span className="w-1 h-1 rounded-full" style={{ backgroundColor: accent }} />
            {g}
          </li>
        ))}
        {gaps.length > 4 && (
          <li className="italic text-[#9CA3AF]">+{gaps.length - 4} more…</li>
        )}
      </ul>

      <div className="grid grid-cols-2 gap-1.5">
        <button onClick={onCopy}
          className="flex items-center justify-center gap-1 px-2 py-1 rounded text-[10px] font-medium border hover:bg-[#F7F5F2]"
          style={{ borderColor: '#E5E1DC', color: '#2A7B88' }}
          data-testid={`missing-day-copy-${date}`}>
          <Clipboard size={10} /> Copy WA
        </button>
        <button onClick={onOpen}
          className="flex items-center justify-center gap-1 px-2 py-1 rounded text-[10px] font-medium text-white hover:opacity-90"
          style={{ backgroundColor: '#185FA5' }}
          data-testid={`missing-day-open-mira-${date}`}>
          Mira <ArrowRight size={10} />
        </button>
      </div>
    </div>
  );
}
